import axios from "axios";
import { tokenManager } from "../utils/tokenManager";

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) return envUrl.replace(/\/$/, "");
  return "http://localhost:8000";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 60000,
  headers: {
    "Accept": "application/json"
  },
  withCredentials: true
});

// Add request logging in development
api.interceptors.request.use(
  (config) => {
    console.log('🔵 API Request:', config.method.toUpperCase(), config.url);

    const token = tokenManager.getToken();
    const isLoginEndpoint = ["/auth/login/", "/auth/lookup/", "/auth/demo-login/", "/auth/send-otp/", "/auth/refresh/"].some(ep => config.url.includes(ep));
    if (token && !isLoginEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 Token added to request');
    } else {
      const publicEndpoints = ["/auth/send-otp/", "/leads/submit/", "/subscriptions/plans/", "/auth/demo-login/", "/auth/login/", "/auth/lookup/"];
      const isPublic = publicEndpoints.some(ep => config.url.includes(ep));
      if (!isPublic) {
        console.warn('⚠️ No auth token found');
      }
    }
    return config;
  },
  (error) => {
    console.error('🔴 Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    console.log('🟢 API Response:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isLoginEndpoint = ["/auth/login/", "/auth/lookup/"].some(ep => originalRequest?.url?.includes(ep));

    const isNetworkSuspended =
      error.code === 'ERR_NETWORK_IO_SUSPENDED' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ERR_CANCELED' ||
      error.code === 'ECONNABORTED' ||
      axios.isCancel(error) ||
      error.message?.includes('Network Error') ||
      error.message?.includes('suspended');

    if (!isLoginEndpoint) {
      if (isNetworkSuspended) {
        console.warn('⚠️ Network request suspended or interrupted:', originalRequest?.url);
      } else {
        console.error('🔴 API Error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
          baseURL: error.config?.baseURL
        });
      }
    }

    // 401 handling - Token Expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = originalRequest.url || "";
      const isLoginEndpoint = ["/auth/login/", "/auth/lookup/", "/auth/demo-login/", "/auth/send-otp/", "/auth/refresh/"].some(ep => url.includes(ep));
      
      console.log(`⚠️ 401 detected on: ${url}. isLoginEndpoint: ${isLoginEndpoint}`);

      // Don't intercept 401 for login/lookup requests as they handle their own errors
      if (isLoginEndpoint) {
        console.log("➡️ Skipping refresh logic for login endpoint");
        return Promise.reject(error);
      }

      // Prevent infinite loops if refresh endpoint itself fails
      if (originalRequest.url.includes('/auth/refresh/')) {
        tokenManager.removeToken();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        console.log("🔄 Attempting Token Refresh...");
        // Use a fresh axios instance to avoid interceptors
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/refresh/`,
          {},
          { withCredentials: true } // Rely on HttpOnly cookie if available
        );

        const newToken = response.data.token || response.data.access;

        if (newToken) {
          console.log("✅ Token Refreshed Successfully");
          tokenManager.setToken(newToken);

          // Update the failed request's header and retry
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error("❌ Token Refresh Failed:", refreshError);
        // If refresh fails, we MUST force logout
        tokenManager.removeToken();
        localStorage.removeItem("user");
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
